import axios from 'axios';
import oauth from 'webmd-oauth/src/index.es6';
import _has from 'lodash/has';
import _isArray from 'lodash/isArray';
import { defineStore } from 'pinia';
import utils from '../helpers/utils.es6';
import { useMainStore } from './root.es6';
import { useCmsStore } from './cms.es6';

const endpoint = 'runtime_flowinfo';
const oauthHeaders = oauth.generate();

function getLifecycle(domain) {
	if (typeof window !== 'undefined') {
		const host = window.location.host.toLowerCase();

		if (host.indexOf('.preview') > -1) {
			return 'preview';
		}
		if (host.indexOf('.staging') > -1 || host.indexOf('localhost') > -1) {
			return 'staging';
		}

		return 'live';
	}

	const host = domain.toLowerCase();

	if (host.indexOf('.preview') > -1) {
		return 'preview';
	}
	if (host.indexOf('.staging') > -1 || host.indexOf('localhost') > -1) {
		return 'staging';
	}

	return 'live';
}

// eslint-disable-next-line no-unused-vars
function getRuleApi(domain) {
	let host;

	if (typeof window !== 'undefined') {
		host = window.location.host.toLowerCase();
	} else {
		host = domain.toLowerCase();
	}

	if (host.indexOf('.devint') > -1 || host.indexOf('localhost') > -1) {
		return 'https://www.devint.webmd.com/kapi/getnextassetrule';
	}
	if (host.indexOf('.qa') > -1) {
		return 'https://www.qa.webmd.com/kapi/getnextassetrule';
	}

	return 'https://www.webmd.com/kapi/getnextassetrule';
}

const actions = {
	// eslint-disable-next-line no-unused-vars
	getFlowInfoForAsset({
		// eslint-disable-next-line no-unused-vars
		route,
		host,
		// eslint-disable-next-line consistent-return
	}) {
		const rootState = useMainStore();
		const apiUrl = `${utils.getEnvironment(host).servicedomain}/search/2/api/${endpoint}`;
		const params = {
			id: route.params.articleid,
			lifecycle: getLifecycle(host),
		};

		try {
			return axios.get(apiUrl, {
				params,
				headers: oauthHeaders,
			})
				.then((response) => {
					/*
					 * PPE-389445 - suppressing IA experience for Content Affiliate content
					 * PPE-414141 - suppressing IA for real pages
					 */
					if (_has(rootState, 'pagedata.omniture.s_package_type') && _has(rootState, 'pagedata.omniture.s_object_type')
						&& (rootState.pagedata.omniture.s_package_type.toLowerCase()
							.includes('content affiliate')
							|| rootState.pagedata.omniture.s_object_type.toLowerCase() === 'wbmd_pb_page')) {
						this.RESET_STATE();
						return false;
					}

					if (response.data && response.data.code === 100) {
						this.SET_DATA(response.data.results);
						return true;
					}
					// eslint-disable-next-line no-param-reassign
					rootState.route = { ...rootState.route, ...route };
					this.SET_GUIDE_BUNDLE(rootState);

					if (!this.flowInfo) {
						try {
							return axios.get(getRuleApi(host), {
								params: {
									cid: route.params.channelid,
									brid: rootState.pagedata.omniture.busref_id,
									lifecycle: getLifecycle(host),
								},
							})
								.then((resp) => {
									if (resp.data) {
										this.SET_DATA({ flowRule: resp.data });
									}
									return true;
								})
								.catch((err) => {
									// eslint-disable-next-line no-console
									console.log(err);
									return true;
								});
						} catch (e) {
							this.RESET_STATE();
							return false;
						}
					}

					return true;
				});
		} catch (e) {
			this.RESET_STATE();
			return false;
		}
	},

	setMetadata(artnum) {
		const rootState = useMainStore();
		// eslint-disable-next-line no-mixed-operators
		const busRef = rootState.pagedata && rootState.pagedata.omniture && rootState.pagedata.omniture.s_business_reference.toLowerCase() || '';
		// eslint-disable-next-line no-mixed-operators
		const objType = rootState.pagedata && rootState.pagedata.omniture && rootState.pagedata.omniture.s_object_type || '';

		if (objType !== 'wbmd_pb_page'
			&& (this.flowInfo || busRef.includes('news') || busRef === 'feature' || busRef === 'medical reference')) {
			// if (rootState.pagedata && rootState.pagedata.omniture && state.flowInfo) {
			// eslint-disable-next-line no-param-reassign
			rootState.pagedata.omniture.s_infinite_article = 'true';
			// to get first article src info this variable is used only for alloy on initial pv call
			rootState.pagedata.omniture.s_next_assetsrc = this.getNextAssetSource ? 'inf-1-'+this.getNextAssetSource : null;
			// eslint-disable-next-line no-param-reassign
			rootState.pagedata.ad.targets.tex = utils.addTexValue(rootState.pagedata.ad.targets.tex, 'inf');
			rootState.pagedata.ad.targets.pg = artnum ? artnum+'-1' : '1-1';

			if (this.flowInfo && this.flowInfo.articleBundle) {
				// eslint-disable-next-line no-param-reassign
				rootState.pagedata.omniture.s_tpcot = `${this.flowInfo.articleBundle.flowId} - ${this.flowInfo.articleBundle.bundleTitle}`;
				// eslint-disable-next-line no-param-reassign
				rootState.pagedata.omniture.s_pbnm = this.flowInfo.articleBundle.bundleType;
			}
		}
	},
	// eslint-disable-next-line no-shadow
	SET_DATA(data) {
		// eslint-disable-next-line no-param-reassign
		this.flowInfo = data;
	},
	// eslint-disable-next-line no-shadow,consistent-return
	SET_GUIDE_BUNDLE() {
		const rootState = useMainStore();
		const cmsStore = useCmsStore();
		const shared = cmsStore.modules.shared_object;
		const module = _has(shared, 'article_publication') ? shared : {};
		const {
			articleid,
			channelid,
			capid,
		} = rootState.route.params;

		this.flowInfo = {};

		if (Object.keys(module).length === 0) {
			this.flowInfo = false;
			return false;
		}

		const guideItems = module && _has(module, 'Guide_Article_Data.content_section.cons_guide_outline.chapter_groups.chapter_group')
			&& _isArray(module.Guide_Article_Data.content_section
				.cons_guide_outline.chapter_groups.chapter_group)
			? module.Guide_Article_Data.content_section.cons_guide_outline.chapter_groups.chapter_group
			: [];

		// getting data for bundle
		const bundleItems = module && _has(module, 'Bundle_Article_Data.content_section.cons_topic_outline.section_groups.section_group')
			&& _isArray(module.Bundle_Article_Data
				.content_section.cons_topic_outline.section_groups.section_group)
			? module.Bundle_Article_Data.content_section
				.cons_topic_outline.section_groups.section_group.map((link) => ({
					text: link.section_title || '',
					// eslint-disable-next-line no-underscore-dangle
					link: link.section_link.link_friendlyUrl || '',
					/* eslint no-underscore-dangle: 0 */
					chronicId: link.section_link._chronic_id || '',
					objectType: link.section_link.object_type || '',
				}))
			: [];

		let items = {};

		// recognizing next article data (guide or bundle)
		if (items && _has(module, 'article_publication') && module.article_publication === 'WebMD Guide') {
			// Getting next article data for guide
			let articleGroupArray = [];
			if (guideItems && guideItems.length > 0) {
				for (let i = 0; i < guideItems.length; i += 1) {
					const sectionGroups = guideItems[i].chapter_link
						.cons_guide_chapter_outline.section_groups;
					const sectionTitle = guideItems[i]?.chapter_link?.cons_guide_chapter_outline?.chapter_title ?? '';
					// eslint-disable-next-line max-len
					if (sectionGroups && _has(sectionGroups, 'section_group') && _isArray(sectionGroups.section_group)) {
						const groupArray = sectionGroups.section_group;
						for (let k = 0; k < groupArray.length; k += 1) {
							const artGroup = groupArray[k]?.article_groups?.article_group;
							if(artGroup && _isArray(artGroup)) {
								artGroup.forEach((obj, index) => {
									artGroup[index].chapter_title = sectionTitle;
								});
							} else {
								artGroup.chapter_title = sectionTitle;
							}
							articleGroupArray = articleGroupArray
								.concat(artGroup);
						}
					} else {
						if (sectionGroups && _has(sectionGroups, 'section_group.article_groups.article_group') 
							&& _isArray(sectionGroups.section_group.article_groups.article_group)) {
							const groupArray = sectionGroups.section_group.article_groups.article_group;
							groupArray.forEach((obj, index) => {
								groupArray[index].chapter_title = sectionTitle;
							});
							articleGroupArray = articleGroupArray
								.concat(groupArray);
						} else {
							const artGroup = sectionGroups.section_group?.article_groups?.article_group;
							artGroup.chapter_title = sectionTitle;
							articleGroupArray = articleGroupArray
								.concat(artGroup);
						}
					}
					/* eslint no-underscore-dangle: 0 */
					const activeIndex = articleGroupArray.findIndex(
						(each) => each.article_link._chronic_id === articleid,
					);
					const activearray = articleGroupArray.find(
						(each) => each.article_link._chronic_id === articleid,
					);
					if (activeIndex !== -1) {
						// If at the end, find the first article in the chapter that is not a pointer
						if (activeIndex + 1 === articleGroupArray.length) {
							const nextarr = articleGroupArray.find(
								(e) => e.article_link.chapter__chronic_id
									=== activearray.article_link.chapter__chronic_id && !['wbmd_cons_ptr', 'pointer_external_reference'].includes(activearray.article_link.object_type),
							);

							items.assetId = nextarr.article_link?._chronic_id ?? '';
							items.capId = capid;
							items.ctgryTitle = nextarr.chapter_title;
							items.ctgryUrl = nextarr.article_link?.link_friendlyUrl ?? '';
							items.category = 'gd'
							items.channelId = channelid;
							items.contentType = 'Article';
							break;
						} else {
							// If not at the end, find the next article that is not a pointer
							let item1 = articleGroupArray.find((a, idx) => idx > activeIndex && !['wbmd_cons_ptr', 'pointer_external_reference'].includes(a.article_link.object_type));

							/*
							 * If there is no article later in the chapter that is not a pointer,
							 * start back from the beginning like above
							 */
							if (!item1) {
								item1 = articleGroupArray.find(
									(e) => e.article_link.chapter__chronic_id
										=== activearray.article_link.chapter__chronic_id && !['wbmd_cons_ptr', 'pointer_external_reference'].includes(activearray.article_link.object_type),
								);
							}

							items.assetId = item1.article_link?._chronic_id ?? '';
							items.capId = capid;
							items.channelId = channelid;
							items.ctgryTitle = item1.chapter_title;
							items.ctgryUrl = item1.article_link?.link_friendlyUrl ?? '';
							items.category = 'gd'
							items.contentType = 'Article';
						}
					}
				}
			}

			if (items.assetId) {
				this.flowInfo.guideArticle = items;
			} else {
				this.flowInfo = false;
			}
		} else {
			// Getting next article data for bundle
			// eslint-disable-next-line max-len
			const activeIndex = bundleItems && bundleItems.findIndex((each) => each.chronicId === articleid);

			if (activeIndex !== -1) {
				let nextArticle = bundleItems.find((b, idx) => idx > activeIndex && !['wbmd_cons_ptr', 'pointer_external_reference'].includes(b.objectType));
				if (!nextArticle) {
					nextArticle = bundleItems.find((b) => !['wbmd_cons_ptr', 'pointer_external_reference'].includes(b.objectType !== 'wbmd_cons_ptr'));
				}

				items = { assetId: nextArticle.chronicId };
				items.capId = capid;
				items.ctgryTitle = bundleItems[activeIndex]?.text ?? '';
				items.ctgryUrl = nextArticle.link;
				items.channelId = channelid;
				items.category = 'bndl';
				items.contentType = 'Article';
			} else {
				items = false;
			}

			if (items) {
				this.flowInfo.legacyBundleArticle = items;
			} else {
				this.flowInfo = false;
			}
		}
	},
	// eslint-disable-next-line no-shadow
	RESET_STATE() {
		// eslint-disable-next-line no-param-reassign
		this.flowInfo = false;
	},
};

const getters = {
	// eslint-disable-next-line no-shadow
	getNextAssetInfo: (state) => (currentAssetId) => {
		// if fed, get the next asset in the list
		if (state.flowInfo.fed) {
			const {
				assets,
				fedConfig,
			} = state.flowInfo.fed;
			const activeIndex = assets.findIndex((a) => a.assetId === currentAssetId);
			const allowedTypes = ['Article', 'Slideshow'];

			// eslint-disable-next-line max-len
			let nextArticle = assets.find((b, idx) => idx > activeIndex && (allowedTypes.includes(b.customType) || allowedTypes.includes(b.type)));
			if (!nextArticle) {
				// eslint-disable-next-line max-len
				nextArticle = assets.find((b) => allowedTypes.includes(b.customType) || allowedTypes.includes(b.type));
			}

			nextArticle.type = nextArticle.type === 'Slideshow' ? 'Slideshow' : 'Article';

			return {
				contentType: nextArticle.type,
				assetId: nextArticle.assetId,
				capId: nextArticle.capid,
				link: nextArticle.link,
				title: nextArticle.title,
				channelId: fedConfig.programid,
				fedConfig,
			};
		}

		// If infinite article, return the data
		if (state.flowInfo.infiniteArticle) {
			return state.flowInfo.infiniteArticle;
		}

		// If freeflow, return the data
		if (state.flowInfo.freeflow) {
			return state.flowInfo.freeflow;
		}

		// If seamless SS, return the data
		if (state.flowInfo.seamlessSlideshow) {
			state.flowInfo.seamlessSlideshow.contentType = 'Slideshow';
			return state.flowInfo.seamlessSlideshow;
		}

		// If guide article SS, return the data
		if (state.flowInfo.guideArticle) {
			return state.flowInfo.guideArticle;
		}

		// If seamless SS, return the data
		if (state.flowInfo.legacyBundleArticle) {
			return state.flowInfo.legacyBundleArticle;
		}

		// If flow rule, return the data
		if (state.flowInfo.flowRule) {
			return state.flowInfo.flowRule;
		}

		// if bundle, get the next asset in the list
		if (state.flowInfo.articleBundle) {
			const { assets } = state.flowInfo.articleBundle;
			const thisIndex = assets.findIndex((a) => a.assetId === currentAssetId);

			if (thisIndex < assets.length - 1) {
				return assets[thisIndex + 1];
			}
			return assets[0];
		}

		return false;
	},

	// eslint-disable-next-line no-shadow
	getNextAssetSource: (state) => {
		const rootState = useMainStore();
		// eslint-disable-next-line no-mixed-operators
		const busRef = rootState.pagedata && rootState.pagedata.omniture.s_business_reference.toLowerCase() || '';

		if (state.flowInfo.fed) {
			return 'fed';
		}

		if (busRef.includes('blog post')) {
			return 'blog';
		}

		if (state.flowInfo.infiniteArticle) {
			return 'ia';
		}

		if (state.flowInfo.freeflow) {
			return 'ff';
		}

		if (state.flowInfo.seamlessSlideshow) {
			return 'ss';
		}

		if (state.flowInfo.guideArticle) {
			return 'gd';
		}

		// If seamless SS, return the data
		if (state.flowInfo.legacyBundleArticle) {
			return 'bndl';
		}

		// If flow rule, return the data
		if (state.flowInfo.flowRule) {
			return 'fr';
		}

		return (busRef.includes('news') || busRef === 'feature' || busRef === 'medical reference') ? 'arec' : null;
	},

	// eslint-disable-next-line no-shadow
	isBundle: (state) => !!state.flowInfo.articleBundle,

	// eslint-disable-next-line no-shadow
	isInfinite: (state) => {
		// adding to test recommendations for news and features, which will therefore ALWAYS be infinite
		// eslint-disable-next-line no-mixed-operators
		const rootState = useMainStore();
		// eslint-disable-next-line no-mixed-operators
		const busRef = rootState.pagedata && rootState.pagedata.omniture.s_business_reference.toLowerCase() || '';

		if (state.flowInfo) {
			return true;
		}

		return busRef.includes('news') || busRef === 'feature' || busRef === 'medical reference';
	},
};

// eslint-disable-next-line import/prefer-default-export
export const useFlowStore = defineStore({
	id: 'flow',
	state: () => ({
		flowInfo: {},
	}),
	getters,
	actions,
});
